// Kit común de gráficas (Recharts) — mismo estilo en toda la app.
//
// Patrón de shadcn/ui charts: Recharts debajo, pero colores desde tokens de
// tema (--chart-*, ver index.css), ejes y rejilla discretos y un tooltip de
// cristal propio. Así las gráficas cambian solas entre el tema Clásico y Tech
// y ninguna pantalla vuelve a meter colores sueltos.
import { cn } from "@/lib/utils";

const v = (token) => `hsl(var(--${token}))`;

export const CHART = {
  mostaza: v("chart-1"),
  gunmetal: v("chart-2"),
  bronce: v("chart-3"),
  topaz: v("chart-4"),
  pizarra: v("chart-5"),
  historico: v("chart-hist"),
  profit: v("profit"),
  loss: v("loss"),
  texto: v("foreground"),
  muted: v("muted-foreground"),
  borde: v("border"),
};

// Serie de colores para años / categorías, por orden.
export const CHART_SERIE = [CHART.mostaza, CHART.gunmetal, CHART.bronce, CHART.topaz, CHART.pizarra];

// Cifras cortas para ejes y etiquetas: 4,8 M · 612 k · 950
const nf1 = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 1 });
const nf0 = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 });
export const fmtCompact = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "";
  const a = Math.abs(n);
  if (a >= 1e6) return `${nf1.format(n / 1e6)} M`;
  if (a >= 1e3) return `${nf0.format(n / 1e3)} k`;
  return nf0.format(n);
};

// Props para <XAxis>/<YAxis>: sin líneas de eje, ticks pequeños y suaves.
export const axisProps = {
  axisLine: false,
  tickLine: false,
  tick: { fill: CHART.muted, fontSize: 12 },
  tickMargin: 8,
};

// Props para <CartesianGrid>: solo líneas horizontales, muy finas.
export const gridProps = {
  vertical: false,
  stroke: CHART.borde,
  strokeOpacity: 0.7,
};

// Degradado vertical para áreas y barras: <defs><ChartGradient id=… color=…/></defs>
export function ChartGradient({ id, color, from = 0.35, to = 0.02 }) {
  return (
    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={color} stopOpacity={from} />
      <stop offset="100%" stopColor={color} stopOpacity={to} />
    </linearGradient>
  );
}

// Tooltip de cristal. Uso: <Tooltip content={<ChartTooltip formatter={fn} />} />
// formatter(value, name, item) → string; labelFormatter(label) → string.
export function ChartTooltip({ active, payload, label, formatter, labelFormatter, hideLabel }) {
  if (!active || !payload || payload.length === 0) return null;
  const titulo = labelFormatter ? labelFormatter(label, payload) : label;
  return (
    <div className="chart-tooltip min-w-[10rem] rounded-xl px-3 py-2.5 text-xs">
      {!hideLabel && titulo !== undefined && titulo !== null && titulo !== "" && (
        <div className="mb-1.5 font-semibold text-foreground">{titulo}</div>
      )}
      <div className="space-y-1">
        {payload
          .filter((p) => p.value !== null && p.value !== undefined)
          .map((p, i) => {
            const color = p.payload?.fill || p.color || p.fill || CHART.mostaza;
            const valor = formatter ? formatter(p.value, p.name, p) : p.value;
            const [texto, nombre] = Array.isArray(valor) ? valor : [valor, p.name];
            return (
              <div key={`${p.dataKey}-${i}`} className="flex items-center gap-2">
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: color }} />
                <span className="flex-1 text-muted-foreground">{nombre}</span>
                <span className="font-medium tabular-nums text-foreground">{texto}</span>
              </div>
            );
          })}
      </div>
    </div>
  );
}

// Leyenda en píldoras. Uso: <Legend content={<ChartLegend />} />
export function ChartLegend({ payload, formatter, className }) {
  if (!payload || payload.length === 0) return null;
  return (
    <div className={cn("flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 pt-3 text-xs", className)}>
      {payload.map((p, i) => (
        <span key={`${p.value}-${i}`} className="inline-flex items-center gap-1.5 text-muted-foreground">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          {formatter ? formatter(p.value) : p.value}
        </span>
      ))}
    </div>
  );
}

// Cursor suave al pasar por barras: <Tooltip cursor={barCursor} …/>
export const barCursor = { fill: CHART.muted, fillOpacity: 0.08, radius: 8 };
export const lineCursor = { stroke: CHART.muted, strokeOpacity: 0.35, strokeDasharray: "4 4" };
